import {
  V2Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  V2Label,
  Select,
  SelectTrigger,
  SelectValue,
  SelectItem,
  SelectContent,
  Button,
  cn,
  Tabs,
  TabsList,
  TabsTrigger,
  DialogFooter,
} from "web-design-system";
import {
  useGenerateReportFile,
  useMfCentralStatus,
  webDownload,
} from "creso-core/hooks";
import { format } from "date-fns";
import { RefreshCwIcon } from "lucide-react";
import { useCallback, useMemo, useRef, useState, type ReactNode } from "react";
import { toast } from "sonner";
import { getFinancialYear } from "creso-core/utils";
import { Loader, useDownloadPdfReport } from "../../hooks";
import {
  createZipCollector,
  getPdfDownloadUrl,
  handleError,
  type ZipArchive,
} from "../../utils";
import type { PdfDownloader, ReportClient, ReportFamily } from "./types";
import {
  downloadFamilyCapitalGainsZip,
  getFamilyCapitalGainsMembers,
} from "./download-family-capital-gains-zip";

export type CapitalGainsSource = "INTERNAL" | "EXTERNAL" | "OVERALL";

export type CapitalGainsDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /**
   * Host-app hook for the "refresh holdings" affordance. The refresh button is
   * only rendered when this is supplied (client mode only).
   */
  onImportHoldings?: () => void;
  /**
   * Host-app account picker, rendered full width as the first form row.
   */
  accountPicker?: ReactNode;
} & (
  | { client: ReportClient; family?: never; createZip?: never }
  | {
      family: ReportFamily;
      client?: never;
      /** Supplies the zip the member reports go into, keeping `jszip` in the host app. */
      createZip: () => ZipArchive;
    }
);

const SOURCE = [
  {
    label: "Internal",
    value: "INTERNAL",
  },
  {
    label: "External",
    value: "EXTERNAL",
  },
  {
    label: "All",
    value: "OVERALL",
  },
] as const;

function getMemberInvestmentAccount(member: {
  investment_account_id?: string | null;
}) {
  return member.investment_account_id || "";
}

export function CapitalGainsDialog(props: CapitalGainsDialogProps) {
  const { family, client, open, onOpenChange, onImportHoldings } = props;
  const { createZip, accountPicker } = props;
  const isFamilyMode = !!family;

  const investmentAccount = isFamilyMode
    ? getMemberInvestmentAccount(family.members?.[0] ?? {})
    : (client?.investment_account ?? "");

  const { data: mfCentralStatus } = useMfCentralStatus(
    investmentAccount,
    !!investmentAccount
  );

  const { mutateAsync: generateReportFile } = useGenerateReportFile();

  const currentFinancialYear = useMemo(() => getFinancialYear(new Date()), []);
  const [selectedFy, setSelectedFy] = useState<number>(currentFinancialYear);
  const [fileType, setFileType] = useState<"json" | "excel" | "csv">("excel");
  const [selectedSource, setSelectedSource] =
    useState<CapitalGainsSource>("INTERNAL");
  const [isFamilyGenerating, setIsFamilyGenerating] = useState(false);
  const [isGeneratingExcel, setIsGeneratingExcel] = useState(false);

  const downloaderRef = useRef<PdfDownloader>(webDownload);
  const pdfDownloader = useCallback<PdfDownloader>(async (blob, fileName) => {
    await downloaderRef.current(blob, fileName);
  }, []);
  const [downloadPdfReport, isGeneratingPdf] =
    useDownloadPdfReport(pdfDownloader);

  const FY_YEARS = Array.from(
    { length: 3 },
    (_, i) => currentFinancialYear - i
  );

  const handleFamilyDownload = async () => {
    if (!family || !createZip) return;

    const toastId = toast.loading("Generating capital gains reports...");
    const zip = createZip();
    // Member PDFs are collected into the zip instead of downloaded one by one.
    downloaderRef.current = createZipCollector(zip);

    try {
      setIsFamilyGenerating(true);
      await downloadFamilyCapitalGainsZip({
        familyName: family.name,
        members: getFamilyCapitalGainsMembers(family.members),
        selectedFy,
        fileType,
        selectedSource,
        zip,
        generateReportFile: async (payload) => {
          const result = await generateReportFile(payload);
          if ("report_url" in result && result.report_url) {
            return { report_url: result.report_url };
          }
          throw new Error("Failed to generate capital gains report");
        },
        generatePdf: async (clientId, fromDate, toDate, fileName) => {
          await downloadPdfReport(
            getPdfDownloadUrl(
              `/reports/clients/${clientId}/capital-gains?start_date=${fromDate}&end_date=${toDate}&source=${selectedSource}`
            ),
            fileName,
            { hideToast: true }
          );
        },
        onProgress: (current, total) => {
          toast.loading(
            `Generating capital gains reports (${current}/${total})...`,
            { id: toastId }
          );
        },
      });
      toast.success("Capital gains reports downloaded successfully", {
        id: toastId,
      });
    } catch (error) {
      toast.dismiss(toastId);
      handleError(error, "Failed to generate capital gains reports");
    } finally {
      downloaderRef.current = webDownload;
      setIsFamilyGenerating(false);
    }
  };

  const handleClientDownload = async () => {
    if (!client) return;

    try {
      const fromDate = format(new Date(selectedFy, 3, 1), "yyyy-MM-dd");
      const toDate = format(new Date(selectedFy + 1, 2, 31), "yyyy-MM-dd");
      if (fileType === "json") {
        await downloadPdfReport(
          getPdfDownloadUrl(
            `/reports/clients/${client.id}/capital-gains?start_date=${fromDate}&end_date=${toDate}&source=${selectedSource}`
          ),
          `${client.name}-FY${selectedFy}-${selectedFy + 1}-capital-gains.pdf`,
          {
            loading: "Generating capital gains report",
          }
        );
        // Don't show success message here - the hook will handle it
      } else {
        setIsGeneratingExcel(true);
        const toastId = toast.loading(
          <Loader
            title="Generating capital gains report"
            description="This may take upto 10 seconds"
          />
        );
        try {
          const result = await generateReportFile({
            file_type: fileType,
            start_date: fromDate,
            end_date: toDate,
            investment_account_guid: client.investment_account ?? "",
            type: "capital_gains",
            source: selectedSource,
          });
          if (!("report_url" in result) || !result.report_url) {
            throw new Error("Failed to generate capital gains report");
          }
          const reportUrl = result.report_url;
          const fileExtension = reportUrl.split(".").pop();
          const response = await fetch(
            `${reportUrl}?source=${selectedSource}&q=${new Date().toISOString()}`
          );
          if (!response.ok) {
            throw new Error("Failed to generate capital gains report");
          }
          const blob = await response.blob();
          const url = URL.createObjectURL(blob);
          const a = document.createElement("a");
          a.href = url;
          a.download = `${client.name}-${format(new Date(), "dd-MM-yyyy")}-capital-gains.${fileExtension}`;
          a.click();
          URL.revokeObjectURL(url);
          toast.success("Capital gains report generated successfully", {
            id: toastId,
          });
        } catch (error) {
          toast.dismiss(toastId);
          throw error;
        } finally {
          setIsGeneratingExcel(false);
        }
      }
    } catch (e) {
      handleError(e, "Failed to generate capital gains report");
    }
  };

  const isDownloading = isFamilyMode
    ? isFamilyGenerating
    : isGeneratingPdf || isGeneratingExcel;

  return (
    <V2Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="p-6 w-100 gap-8">
        <DialogHeader>
          <DialogTitle className="!text-lg">Capital gains report</DialogTitle>
          <DialogDescription className="text-muted-foreground gap-2 flex flex-row items-center text-sm">
            {mfCentralStatus?.last_fetch_at
              ? `Holdings last updated on ${format(mfCentralStatus.last_fetch_at, "dd MMM yyyy")}`
              : ""}
            {!isFamilyMode &&
            onImportHoldings &&
            mfCentralStatus?.last_fetch_at ? (
              <Button onClick={onImportHoldings} size="icon" variant="ghost">
                <RefreshCwIcon className={cn("size-4")} />
              </Button>
            ) : null}
          </DialogDescription>
        </DialogHeader>
        <div className="flex flex-col gap-8 -mt-4">
          {accountPicker}
          <div className="flex flex-row items-center justify-between gap-3 w-full">
            <V2Label
              className={cn("shrink-0 whitespace-nowrap text-sm font-normal")}
            >
              Financial year
            </V2Label>
            <div className="min-w-0 max-w-56 flex-1">
              <Select
                value={selectedFy.toString()}
                onValueChange={(v) => setSelectedFy(parseInt(v, 10))}
              >
                <SelectTrigger className="w-full">
                  <SelectValue placeholder="Select financial year" />
                </SelectTrigger>
                <SelectContent>
                  {FY_YEARS.map((year) => (
                    <SelectItem key={year} value={year.toString()}>
                      Apr {year} - Mar {year + 1}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="flex flex-row items-center justify-between gap-3">
            <V2Label
              className={cn("shrink-0 whitespace-nowrap text-sm font-normal")}
            >
              Source
            </V2Label>
            <div className="min-w-0 max-w-56 flex-1">
              <Tabs
                value={selectedSource}
                onValueChange={(value) =>
                  setSelectedSource(value as CapitalGainsSource)
                }
              >
                <TabsList className="w-full justify-start">
                  {SOURCE.map((s) => (
                    <TabsTrigger key={s.value} value={s.value}>
                      {s.label}
                    </TabsTrigger>
                  ))}
                </TabsList>
              </Tabs>
            </div>
          </div>
          <div className="flex flex-row items-center justify-between gap-3">
            <V2Label
              className={cn("shrink-0 whitespace-nowrap text-sm font-normal")}
            >
              File type
            </V2Label>
            <div className="min-w-0 max-w-56 flex-1">
              <Tabs
                value={fileType}
                onValueChange={(v) =>
                  setFileType(v as "json" | "excel" | "csv")
                }
              >
                <TabsList className="w-full justify-start">
                  <TabsTrigger value="excel" className="w-full">
                    Excel
                  </TabsTrigger>
                  <TabsTrigger value="json" className="w-full">
                    PDF
                  </TabsTrigger>
                </TabsList>
              </Tabs>
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button
            className="w-full"
            onClick={isFamilyMode ? handleFamilyDownload : handleClientDownload}
            disabled={isDownloading}
          >
            {isDownloading ? "Generating..." : "Download"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </V2Dialog>
  );
}
